import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthRequest } from '../models/auth-request';
import { AuthResponse, IRefreshResponse } from '../models/auth-response';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { APIResponse } from 'src/app/core/models/api-response';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);

  login(payload: AuthRequest): Observable<APIResponse<AuthResponse>> {
    const httpOptions = {
      params: new HttpParams().set('database', payload?.database || ''),
    };
    return this.http.post<APIResponse<AuthResponse>>(
      environment.BASE_URL_API + 'retailers/auth/login',
      { email: payload.email, password: payload.password },
      httpOptions
    );
  }

  /**
   * Exchange a refresh token for a new access token.
   *
   * Rotated server-side: the token sent is consumed and a replacement comes
   * back. Callers MUST store it - replaying a consumed token revokes the
   * session.
   */
  refreshAccessToken(refreshToken: string): Observable<APIResponse<IRefreshResponse>> {
    return this.http.post<APIResponse<IRefreshResponse>>(
      `${environment.BASE_URL_API}retailers/auth/refresh/token`,
      { refresh_token: refreshToken }
    );
  }

  /**
   * Revoke the session behind *refreshToken* server-side.
   *
   * Always 200, even for an unknown token, so it cannot be used to probe
   * whether a stolen token is still live.
   */
  logout(refreshToken: string): Observable<APIResponse<void>> {
    return this.http.post<APIResponse<void>>(
      `${environment.BASE_URL_API}retailers/auth/logout`,
      { refresh_token: refreshToken }
    );
  }

  getAuthStatus() {
    const currentUser = LocalStorageService.get('currentUser');

    // Sessions stored before the token rename only carry ``token``.
    return !!(currentUser.access_token || currentUser.token);
  }

  public forgetPassword(payload: {
    email: string;
    database?: string;
  }): Observable<APIResponse<void>> {
    return this.http.post<APIResponse<void>>(
      `${environment.BASE_URL_API}auth/forgot-password`,
      { email: payload.email },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        params: new HttpParams().set('database', payload?.database || ''),
      }
    );
  }
  resetPassword(
    newPassword: string,
    confirmPassword: string,
    token: string
  ): Observable<APIResponse<void>> {
    const body = {
      newPassword: newPassword,
      confirmPassword: confirmPassword,
      token: token,
    };

    return this.http.post<APIResponse<void>>(
      `${environment.BASE_URL_API}auth/reset-password`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }
  UpdateContact(
    newName: string,
    newImage: string,
    newLanguage: string
  ): Observable<APIResponse<void>> {
    const body = {
      name: newName,
      image_1920: newImage,
      lang: newLanguage,
    };

    return this.http.post<APIResponse<void>>(
      `${environment.BASE_URL_API}retailers/auth/update`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  getDBs(): Observable<APIResponse<string>> {
    return this.http.get<APIResponse<string>>(`${environment.BASE_URL_API}databases`);
  }

  unsubscribe(contact_id: number): Observable<APIResponse<void>> {
    return this.http.get<APIResponse<void>>(
      `${environment.BASE_URL_API}retailers/${contact_id}/unsubscribe`
    );
  }
}
